import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Col,
  Popconfirm,
  Row,
  Select,
  Table,
  Form,
  Button,
  Spin,
  Space,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { AppContext } from "../../../context/AppContextProvider";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import QuantityStepper from "../../../components/QuantityStepper";
import {
  getBranches,
  getProductStockByBranch,
  updateStockTransfers,
} from "../../../network/network";
import { handleApiError, showAppError } from "../../../utils/errorHandler";
import {
  clampQuantityToAvailableStock,
  isQuantityWithinAvailableStock,
  normalizePositiveQuantity,
} from "../../../utils/inventoryQuantity";

const EditTransfer = () => {
  const { appConfig } = useContext(AppContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchesData, setBranchesData] = useState([]);
  const [adjustmentData, setAdjustmentData] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [branchId, setBranchId] = useState(0);
  let { transferId } = useParams();
  const { t } = useTranslation();
  const { state } = useLocation();
  let navigate = useNavigate();
  const [editTransferForm] = Form.useForm();

  useEffect(() => {
    state
      ? setData(state)
      : navigate("/inventory/stock-transfer/" + transferId);
  }, [navigate, state, transferId]);

  useEffect(() => {
    branchId &&
      getProductStockByBranch(branchId)
        .then((response) => {
          if (response?.data?.success === false) {
            handleApiError(response, t);
            return;
          }

          setProductsList(response.data.data.products);
        })
        .catch((error) => handleApiError(error, t));
  }, [branchId, t]);

  useEffect(() => {
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesData(response.data.data.branches);
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  useEffect(() => {
    if (data.id) {
      setBranchId(data.branch_from.id);
      for (let i = 0; i < data.products.length; i++) {
        setLoading(true);
        for (let j = 0; j < productsList.length; j++) {
          if (data.products[i].product.id === productsList[j].product.id) {
            data.products[i].quantity = productsList[j].quantity;
            data.products[i].qty = data.products[i].requested_quantity;
            setAdjustmentData(data.products);
          }
        }
        setLoading(false);
      }
      editTransferForm.resetFields();
      // Set Form Fields
      editTransferForm.setFieldsValue({
        id: data.id,
        branch_from_id: data.branch_from.id,
        branch_to_id: data.branch_to.id,
      });
    }
  }, [data, editTransferForm, productsList]);

  const adjustmentReducer = (product, action, quantity) => {
    let updatedAdjustment = JSON.parse(JSON.stringify(adjustmentData));
    let productId = product.product.id;
    let indexFound = updatedAdjustment.findIndex(
      (product) => product.product.id === productId,
    );
    if (action === "add") {
      if (indexFound === -1) {
        product.qty = 1;
        updatedAdjustment.push(product);
      } else {
        showAppError(t("product_already_selected"));
      }
    } else if (action === "inc") {
      const item = updatedAdjustment[indexFound];
      item.qty = clampQuantityToAvailableStock(item.qty + 1, item.quantity);
    } else if (action === "dec") {
      updatedAdjustment[indexFound].qty = normalizePositiveQuantity(
        updatedAdjustment[indexFound].qty - 1,
      );
    } else if (action === "remove") {
      updatedAdjustment.splice(indexFound, 1);
    } else if (action === "directInput") {
      const item = updatedAdjustment[indexFound];
      item.qty = clampQuantityToAvailableStock(quantity, item.quantity);
    }
    setAdjustmentData(updatedAdjustment);
  };

  const handleEditTransfer = () => {
    editTransferForm
      .validateFields()
      .then((values) => {
        const hasInvalidQuantity = adjustmentData.some(
          (item) => !isQuantityWithinAvailableStock(item.qty, item.quantity),
        );

        if (hasInvalidQuantity) {
          showAppError(t("insufficient_quantity"));
          return;
        }

        // Set Loading True
        setLoading(true);
        let transactionObj = {
          id: values.id,
          branch_from_id: values.branch_from_id,
          branch_to_id: values.branch_to_id,
        };
        transactionObj.stocks = adjustmentData.map((item) => ({
          product_id: item.product.id,
          requested_quantity: item.qty,
        }));

        // Sending Request to API
        updateStockTransfers(transactionObj)
          .then((response) => {
            let data = response.data || response.response.data;
            if (data.success === true) {
              // Success
              NotificationWithIcon(
                "success",
                t("transfer_updated"),
                t("transfer_updated_successfully"),
              );
              navigate("/inventory/stock-transfer/" + transferId);
            } else {
              handleApiError(response, t);
            }
          })
          .catch((error) => handleApiError(error, t))
          .finally(() => {
            setLoading(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleProductSelect = (value) => {
    const product = productsList.find(
      (product) => product.product.id === value,
    );
    adjustmentReducer(product, "add");
    editTransferForm.setFieldsValue({
      product: "",
    });
  };

  const productTableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("image"),
      width: 85,
      render: (record) => {
        return (
          <>
            <img
              alt={record.product.name_en}
              src={
                record.product.image
                  ? record.product.image
                  : "/images/image-not-found.png"
              }
              className="app-table-thumbnail"
            />
          </>
        );
      },
    },
    {
      title: t("product"),
      render: (record) => {
        return (
          <>
            {appConfig.direction === "ltr"
              ? record.product.name_en
              : record.product.name}
          </>
        );
      },
    },
    {
      title: t("available_stock"),
      align: "center",
      dataIndex: "quantity",
    },
    {
      title: t("quantity"),
      align: "center",
      render: (record) => {
        return (
          <>
            <QuantityStepper
              value={record.qty}
              min={1}
              max={record.quantity}
              showInput
              decreaseDisabled={record.qty <= 1}
              increaseDisabled={record.quantity <= record.qty}
              onDecrease={() => adjustmentReducer(record, "dec")}
              onIncrease={() => adjustmentReducer(record, "inc")}
              onChange={(quantity) =>
                adjustmentReducer(record, "directInput", quantity)
              }
            />
          </>
        );
      },
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (record) => {
        return (
          <Space>
            <Popconfirm
              title={t("warning_remove")}
              onConfirm={() => adjustmentReducer(record, "remove")}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "/inventory/stock-transfer",
      breadcrumbName: t("stock_transfer"),
      icon: <SwapOutlined />,
    },
    {
      path: "edit-transfer",
      breadcrumbName: t("edit_transfer"),
      icon: <EditOutlined />,
    },
  ];

  return (
    <Spin spinning={loading}>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          title={t("edit_transfer")}
          onBack={() => window.history.back()}
          ghost={false}
          className="app-p-0"
        />
      </Card>
      <Card
        style={{
          direction: appConfig.direction
            ? appConfig.direction === "ltr"
              ? "ltr"
              : "rtl"
            : "ltr",
        }}
      >
        {data.id && (
          <Form
            name="Edit-Transer-form"
            layout="vertical"
            onFinish={handleEditTransfer}
            className="app-form-full"
            form={editTransferForm}
          >
            <Form.Item name="id" hidden={true}>
              <input />
            </Form.Item>
            <Row gutter={[20, 20]}>
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="branch_from_id"
                  label={t("branch_from")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_branch_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Select
                    disabled
                    showSearch
                    allowClear
                    onChange={(e) => {
                      setBranchId(e);
                      setAdjustmentData([]);
                    }}
                    options={branchesData
                      .filter((branch) => {
                        if (
                          branch.id !==
                          editTransferForm.getFieldValue("branch_to_id")
                        ) {
                          return branch;
                        } else return null;
                      })
                      .map((branch) => {
                        return {
                          label: branch.name,
                          value: branch.id,
                        };
                      })}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={8}
                lg={8}
                xl={8}
                xxl={8}
              >
                <Form.Item
                  name="branch_to_id"
                  label={t("branch_to") + " (" + t("your_branch") + ")"}
                  rules={[
                    {
                      required: true,
                      message: t("validate_branch_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Select
                    disabled
                    showSearch
                    allowClear
                    options={branchesData.map((branch) => {
                      return {
                        label: branch.name,
                        value: branch.id,
                      };
                    })}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              {branchId ? (
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={8}
                  xxl={8}
                >
                  <Form.Item
                    name="product"
                    label={t("product_add")}
                    rules={[
                      adjustmentData.length < 1 && {
                        required: true,
                        message: t("validate_product_name_required"),
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      options={productsList
                        .filter((product) => {
                          if (product.quantity > 0) {
                            return product;
                          } else return null;
                        })
                        .map((product) => {
                          return {
                            label:
                              appConfig.direction === "ltr"
                                ? `${product.product.name_en} (${product.quantity})`
                                : `${product.product.name} (${product.quantity})`,
                            value: product.product.id,
                          };
                        })}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={handleProductSelect}
                    />
                  </Form.Item>
                </Col>
              ) : (
                ""
              )}
              {adjustmentData.length > 0 && (
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                  xxl={24}
                >
                  <Table
                    columns={productTableColumns}
                    dataSource={adjustmentData}
                    rowKey="id"
                    pagination={false}
                  />
                </Col>
              )}
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                xxl={24}
              >
                <Form.Item className="app-text-center">
                  <Space>
                    <Button
                      type="default"
                      key="back"
                      onClick={() =>
                        navigate("/inventory/stock-transfer/" + transferId)
                      }
                    >
                      {t("cancel")}
                    </Button>
                    <Button type="primary" htmlType="submit">
                      {t("update")}
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Card>
    </Spin>
  );
};

export default EditTransfer;
